import { useLocation } from "react-router";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import type { SanitizedFeedSectionArticle } from "@typings/Cue";
import { cn } from "@util/helpers";

type EventsAwardsNewsListingStoriesProps = {
  article: SanitizedFeedSectionArticle;
  index: number;
  awardType: string;
};

/**
 * Component to render news listing page for event and awards
 */
const EventsAwardsNewsListingStories = ({
  article: {
    media,
    kicker,
    sections,
    id,
    title,
    displaySetting,
    blurb,
    urlPath,
    paywall,
    edited,
    updated,
  },
  index,
}: EventsAwardsNewsListingStoriesProps): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const { isScreenSM } = useWindowSize();
  const { pathname } = useLocation();

  const isSubscriber = OKTAUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;
  const sectionName = pathname.substring(pathname.lastIndexOf("/") + 1);
  const inPodcastsLandingPage = sectionName === "podcasts";
  const isLeadArticle = index === 0 && !inPodcastsLandingPage;
  const firstImage = media?.[0];
  const articleMedia = isLeadArticle && !isScreenSM ? undefined : firstImage;
  const uniqueName = sections?.[0]?.uniqueName || sectionName;

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: "font-public-sans",
    },
    image: {
      width: "w-2/5 lg:w-1/6",
      position: "left",
      extraClass: "lg:order-2",
      filters: { w: 100, h: 100, dpr: 1, f: "webp" },
      aspectRatio: "aspect-3x2",
      imageClassName: cn("object-cover"),
    },
    content: {
      width: "w-3/5 lg:w-5/6",
      extraClass: cn("pl-4 lg:order-1 lg:pl-0 lg:pr-4"),
      blurb: {
        extraClass: cn("text-gray-750 font-open-sans"),
      },
      updatedTimeExtraClass: "text-7xs font-public-sans",
      updatedTimeWrapperClass: "lg:mt-auto",
    },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: "font-public-sans my-1",
    },
  };

  return (
    <div
      data-testdata-testid="events-awards-news-listing-stories-component"
      className={cn("story border-b border-gray-175 py-4")}
    >
      <Card
        id={id}
        title={displaySetting?.displayHeadline || title}
        kicker={kicker?.fields?.[0]?.value}
        media={articleMedia}
        blurb={blurb}
        link={urlPath}
        variations={variant}
        updatedTime={updated}
        editedTime={edited}
        displayPaywall={isSubscriber ? paywall?.contentAccess === "1" : false}
        hasUpdatedTimestampDisplay={displaySetting?.hasUpdatedTimeDisplayed}
        defaultImage={{
          directoryName: uniqueName,
        }}
        className="items-stretch"
      />
    </div>
  );
};

export default EventsAwardsNewsListingStories;
